import { normalizeNewsContent } from "./createCardTransformShared";
import { isValidYoutubeUrl } from "@/shared/utils/youtube/youtubeEmbedUrl";
import type { PageData } from "./useTemplatePageHelpers";
import { TFunction } from "i18next";

/* Validation helpers for template pages.
 * This module checks page content and returns blocking messages for missing or
 * invalid data, without managing any UI state itself.
 */
interface GetTemplatePageValidationMessagesParams {
  page: PageData;
  pageIndex: number;
  isGiftCardTemplate: boolean;
  t: TFunction;
}

const normalizeTypeKey = (type?: string) =>
  (type || "").replace(/_/g, "").replace(/\s+/g, "").toLowerCase().trim();

const stripRichText = (value: string | undefined | null): string =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasMinTextLength = (value: string | undefined | null, minLength = 1): boolean =>
  stripRichText(value).length >= minLength;

const hasNonEmptyString = (value: string | undefined | null): boolean =>
  typeof value === "string" && value.trim() !== "";

const hasFileOrStringValue = (value: string | File | null | undefined): boolean => {
  if (value instanceof File) return true;
  return hasNonEmptyString(value);
};

const toNumber = (value: unknown): number => (typeof value === "number" ? value : Number(value));

const isPositiveNumber = (value: unknown): boolean => {
  const numberValue = toNumber(value);
  return Number.isFinite(numberValue) && numberValue > 0;
};

const isNonNegativeNumber = (value: unknown): boolean => {
  const numberValue = toNumber(value);
  return Number.isFinite(numberValue) && numberValue >= 0;
};

const isIntegerAtLeast = (value: unknown, min: number): boolean => {
  const numberValue = toNumber(value);
  return Number.isFinite(numberValue) && Number.isInteger(numberValue) && numberValue >= min;
};

const isFutureDate = (value: string | undefined | null, minimumOffsetMs = 0): boolean => {
  if (!hasNonEmptyString(value)) return false;
  const timestamp = new Date(value ?? "").getTime();
  return Number.isFinite(timestamp) && timestamp > Date.now() + minimumOffsetMs;
};

const isValidUploadedValue = (value: unknown) => {
  if (value instanceof File) return true;
  if (typeof value !== "string") return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  return (
    /^https?:\/\/.+/i.test(trimmed) || trimmed.startsWith("data:") || trimmed.startsWith("blob:")
  );
};

const getEndingImageUrlFromContent = (content: any) => {
  if (!content) return "";
  if (typeof content.imageUrl === "string" && content.imageUrl.trim()) {
    return content.imageUrl;
  }
  if (typeof content.url === "string" && content.url.trim()) {
    return content.url;
  }
  if (Array.isArray(content.url) && typeof content.url[0] === "string") {
    return content.url[0] || "";
  }
  return "";
};

export const getTemplatePageValidationMessages = ({
  page,
  pageIndex,
  isGiftCardTemplate,
  t,
}: GetTemplatePageValidationMessagesParams): string[] => {
  const typeKey = normalizeTypeKey(page.type);
  const content = page.content || {};
  const messages: string[] = [];

  if (typeKey === "coverimage" || typeKey === "endingimage") {
    const imageUrl = getEndingImageUrlFromContent(content) || content.imageUrl || "";
    if (!hasFileOrStringValue(imageUrl)) {
      messages.push(t("image_required"));
    }
  } else if (typeKey === "text") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
  } else if (typeKey === "about") {
    const hasMoments = (content.moments || []).some((item: string) => item?.trim());
    const hasCredits = (content.credits || []).some((item: string) => item?.trim());
    const extraDescription = content.extraDescription ?? content.extra_description;
    const moodNote = content.moodNote ?? content.mood_note;
    const descriptionImage = content.descriptionImage ?? content.description_image;
    const extraDescriptionImage = content.extraDescriptionImage ?? content.extra_description_image;
    const momentImage = content.momentImage ?? content.moment_image;
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!hasMinTextLength(extraDescription, 10)) {
      messages.push(t("extra_description_min_10"));
    }
    if (!hasMinTextLength(moodNote, 3)) {
      messages.push(t("mood_note_min_3"));
    }
    if (!hasFileOrStringValue(descriptionImage)) {
      messages.push(t("description_image_required"));
    }
    if (!hasFileOrStringValue(extraDescriptionImage)) {
      messages.push(t("extra_description_image_required"));
    }
    if (!hasFileOrStringValue(momentImage)) {
      messages.push(t("moment_image_required"));
    }
    if (!hasMoments) {
      messages.push(t("add_moment"));
    }
    if (!hasCredits) {
      messages.push(t("add_credit"));
    }
  } else if (typeKey === "bonus") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!hasFileOrStringValue(content.image)) {
      messages.push(t("hidden_image_required"));
    }
    if (!hasFileOrStringValue(content.voiceNote || content.voice_note)) {
      messages.push(t("voice_note_required"));
    }
    if (!hasFileOrStringValue(content.demoSnippet || content.demo_snippet)) {
      messages.push(t("demo_snippet_required"));
    }
  } else if (typeKey === "instrument") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.artistName || content.artist_name, 2)) {
      messages.push(t("artist_name_min_2"));
    }
    if (!hasFileOrStringValue(content.url)) {
      messages.push(t("audio_required"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
  } else if (typeKey === "image" || typeKey === "imagegrid") {
    const images = Array.isArray(content.images)
      ? content.images
      : Array.isArray(content.url)
        ? content.url
        : content.imageUrl
          ? [content.imageUrl]
          : typeof content.url === "string" && content.url
            ? [content.url]
            : [];
    if (images.filter(Boolean).length === 0) {
      messages.push(t("add_image"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
  } else if (typeKey === "audio") {
    const audioUrl = typeof content.audioUrl !== "undefined" ? content.audioUrl : content.url || "";
    if (!isValidUploadedValue(audioUrl)) {
      messages.push(t("audio_url_required"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
  } else if (typeKey === "video") {
    const videoUrl = content.videoUrl || content.url || "";
    const isGiftPlaceholderPage = isGiftCardTemplate && pageIndex === 2;
    if (!isGiftPlaceholderPage) {
      if (!hasFileOrStringValue(videoUrl)) {
        messages.push(t("video_required"));
      }
      if (!hasMinTextLength(content.description, 10)) {
        messages.push(t("description_min_10"));
      }
    }
  } else if (typeKey === "instantcard") {
    if (!content.suit) {
      messages.push(t("suit_required"));
    }
    if (!content.mode) {
      messages.push(t("mode_required"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.chip)) {
      messages.push(t("chip_greater_than_0"));
    }
    if (!content.duration) {
      messages.push(t("duration_required"));
    }
    if (content.availability === undefined) {
      messages.push(t("availability_required"));
    }
  } else if (typeKey === "songs") {
    const songs = Array.isArray(content.songs) ? content.songs : [];
    if (!songs.some((song: string) => song?.trim())) {
      messages.push(t("add_song"));
    }
  } else if (typeKey === "song" || typeKey === "song4usongs") {
    const songs = Array.isArray(content.songs) ? content.songs : [];
    if (songs.length < 3) {
      messages.push(t("add_song_min_3"));
    }
    if (songs.some((song: any) => !hasMinTextLength(song?.name, 1))) {
      messages.push(t("song_name_required"));
    }
    if (songs.some((song: any) => !isPositiveNumber(song?.price))) {
      messages.push(t("song_price_greater_than_0"));
    }
  } else if (typeKey === "flashcard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!isFutureDate(content.show_time, 15 * 60 * 1000)) {
      messages.push(t("show_time_future"));
    }
    if (!isNonNegativeNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
    if (
      !Number.isFinite(toNumber(content.duration)) ||
      toNumber(content.duration) < 30 ||
      toNumber(content.duration) > 60
    ) {
      messages.push(t("duration_between_30_60"));
    }
    if (!isIntegerAtLeast(content.minimum_audience, 1)) {
      messages.push(t("minimum_audience_min_1"));
    }
    if (content.allow_observer === undefined) {
      messages.push(t("allow_observer_required"));
    }
  } else if (typeKey === "stagecard") {
    if (!content.type) {
      messages.push(t("type_required"));
    }
    if (!hasMinTextLength(content.title, 1)) {
      messages.push(t("title_required"));
    }
    if (!hasMinTextLength(content.short_description, 1)) {
      messages.push(t("short_description_required"));
    }
    if (!content.venue_size) {
      messages.push(t("venue_size_required"));
    }
    if (!hasNonEmptyString(content.category)) {
      messages.push(t("category_required"));
    }
    if (!hasNonEmptyString(content.sub_category)) {
      messages.push(t("sub_category_required"));
    }
    if (!hasNonEmptyString(content.banner_title)) {
      messages.push(t("banner_title_required"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
    if (!isPositiveNumber(content.duration)) {
      messages.push(t("duration_greater_than_0"));
    }
  } else if (typeKey === "staicard") {
    if (
      !hasMinTextLength(content.talent_name, 2) ||
      stripRichText(content.talent_name).length > 100
    ) {
      messages.push(t("talent_name_between_2_100"));
    }
    if (!hasMinTextLength(content.title, 3) || stripRichText(content.title).length > 100) {
      messages.push(t("title_between_3_100"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isNonNegativeNumber(content.temporary_price)) {
      messages.push(t("temporary_price_greater_than_0"));
    }
    if (!isNonNegativeNumber(content.permanent_price)) {
      messages.push(t("permanent_price_greater_than_0"));
    }
    if (
      !Number.isFinite(toNumber(content.duration)) ||
      toNumber(content.duration) < 1 ||
      toNumber(content.duration) > 7
    ) {
      messages.push(t("duration_between_1_7"));
    }
    if (!isFutureDate(content.start_time, 5 * 60 * 1000)) {
      messages.push(t("start_time_future"));
    }
    if (!hasMinTextLength(content.thankyou_message, 1)) {
      messages.push(t("thankyou_message_required"));
    }
    if (!hasFileOrStringValue(content.signature)) {
      messages.push(t("signature_image_required"));
    }
  } else if (
    typeKey === "taascard" ||
    typeKey === "diamondcard" ||
    typeKey === "clovercard" ||
    typeKey === "heartcard" ||
    typeKey === "spadecard"
  ) {
    const inferredSuit =
      content.suit ||
      (typeKey === "diamondcard"
        ? "Diamond"
        : typeKey === "clovercard"
          ? "Clover"
          : typeKey === "spadecard"
            ? "Spade"
            : "Heart");
    if (!hasMinTextLength(content.title, 1)) {
      messages.push(t("title_required"));
    }
    if (!hasNonEmptyString(content.category)) {
      messages.push(t("category_required"));
    }
    if (!hasMinTextLength(content.description, 1)) {
      messages.push(t("description_required"));
    }
    if (!content.suit && typeKey === "taascard") {
      messages.push(t("suit_required"));
    }
    if (inferredSuit === "Diamond") {
      const products = Array.isArray(content.products) ? content.products : [];
      if (products.length === 0) {
        messages.push(t("product_required"));
      }
      if (products.some((product: any) => !hasMinTextLength(product?.title, 1))) {
        messages.push(t("product_title_required"));
      }
      if (products.some((product: any) => !hasNonEmptyString(product?.product_type))) {
        messages.push(t("product_type_required"));
      }
      if (
        products.some((product: any) => !hasFileOrStringValue(product?.product_url ?? product?.url))
      ) {
        messages.push(t("product_file_or_url_required"));
      }
    } else if (inferredSuit === "Clover") {
      if (!hasNonEmptyString(content.start_date_time)) {
        messages.push(t("start_date_time_required"));
      }
      if (!hasNonEmptyString(content.banner_title)) {
        messages.push(t("banner_title_required"));
      }
      if (!isPositiveNumber(content.duration)) {
        messages.push(t("duration_min_1"));
      }
    } else {
      if (!isPositiveNumber(content.price)) {
        messages.push(t("price_greater_than_0"));
      }
      if (!isPositiveNumber(content.duration)) {
        messages.push(t("duration_min_1"));
      }
    }
  } else if (typeKey === "buskingcard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasNonEmptyString(content.category)) {
      messages.push(t("category_required"));
    }
    if (!isFutureDate(content.start_time)) {
      messages.push(t("start_time_future"));
    }
  } else if (typeKey === "giftcard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isNonNegativeNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
  } else if (typeKey === "snapflyer" || typeKey === "dearcard") {
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
  } else if (typeKey === "aitcard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
  } else if (typeKey === "song4ucard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
  } else if (typeKey === "bmcard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
    if (!isFutureDate(content.seal_time)) {
      messages.push(t("seal_time_future"));
    }
    if (!hasMinTextLength(content.thankyou_message, 1)) {
      messages.push(t("thankyou_message_required"));
    }
    if (stripRichText(content.thankyou_message).length > 100) {
      messages.push(t("thankyou_message_max_100"));
    }
    if (!hasFileOrStringValue(content.signature_url)) {
      messages.push(t("signature_url_required"));
    }
  } else if (
    typeKey === "democard" ||
    typeKey === "listeningtogethercard" ||
    typeKey === "listentogethercard"
  ) {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
  } else if (typeKey === "singlecard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
  } else if (typeKey === "kindiecard") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isPositiveNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
    if (!isFutureDate(content.seal_time)) {
      messages.push(t("seal_time_future"));
    }
  } else if (typeKey === "postcard" || typeKey === "photocard") {
    if (!hasFileOrStringValue(content.url)) {
      messages.push(t("photocard_image_required"));
    }
  } else if (typeKey === "youtube") {
    if (!hasMinTextLength(content.title, 3)) {
      messages.push(t("title_min_3"));
    }
    if (!(hasNonEmptyString(content.url) && isValidYoutubeUrl(content.url))) {
      messages.push(t("youtube_url_required"));
    }
  } else if (typeKey === "news") {
    const items = normalizeNewsContent(content);
    if (items.length === 0) {
      messages.push(t("news_item_required"));
    }
    if (items.some((item) => !hasMinTextLength(item?.title, 3))) {
      messages.push(t("news_item_title_required"));
    }
    if (items.some((item) => !hasMinTextLength(item?.description, 1))) {
      messages.push(t("news_item_description_required"));
    }
    if (items.some((item) => !hasFileOrStringValue(item?.url as any))) {
      messages.push(t("news_item_image_required"));
    }
  } else if (typeKey === "fanclubcard") {
    if (
      !hasMinTextLength(content.talent_name, 2) ||
      stripRichText(content.talent_name).length > 100
    ) {
      messages.push(t("talent_name_between_2_100"));
    }
    if (!hasMinTextLength(content.title, 3) || stripRichText(content.title).length > 100) {
      messages.push(t("title_between_3_100"));
    }
    if (!hasMinTextLength(content.description, 10)) {
      messages.push(t("description_min_10"));
    }
    if (!isNonNegativeNumber(content.price)) {
      messages.push(t("price_greater_than_0"));
    }
    if (
      !Number.isFinite(toNumber(content.edition_year)) ||
      toNumber(content.edition_year) < 2000 ||
      toNumber(content.edition_year) > 2100
    ) {
      messages.push(t("edition_year_2000_2100"));
    }
    if (!isFutureDate(content.start_time)) {
      messages.push(t("start_time_future"));
    }
    if (!hasMinTextLength(content.thankyou_message, 1)) {
      messages.push(t("thankyou_message_required"));
    }
    if (!hasFileOrStringValue(content.signature)) {
      messages.push(t("signature_image_required"));
    }
  } else if (typeKey === "welcome" || typeKey === "fanclubwelcome") {
    if (!hasMinTextLength(content.title, 3) || stripRichText(content.title).length > 100) {
      messages.push(t("title_between_3_100"));
    }
    if (!hasMinTextLength(content.content, 10)) {
      messages.push(t("welcome_content_min_10"));
    }
  } else if (typeKey === "linkcard" || typeKey === "link_card") {
    const links = Array.isArray(content.links) ? content.links : [];
    const url =
      (typeof content.url === "string" ? content.url : "") ||
      (typeof links[0]?.url === "string" ? links[0].url : "");
    if (!/^https?:\/\/.+/i.test(url.trim())) {
      messages.push(t("link_url_required"));
    }
    const linkTitle =
      (typeof content.title === "string" ? content.title : "") ||
      (typeof links[0]?.title === "string" ? links[0].title : "");
    if (!hasMinTextLength(linkTitle, 3) || stripRichText(linkTitle).length > 100) {
      messages.push(t("title_between_3_100"));
    }
    const price = content.price != null && content.price !== "" ? String(content.price).trim() : "";
    if (price && !/^\d+(\.\d{1,2})?$/.test(price)) {
      messages.push(t("price_invalid"));
    }
  }

  return Array.from(new Set(messages));
};
